<template>
    <div class="command-palette-wrapper">
        <ninja-keys
            ref="ninjaKeys"
            :data="commands"
            :placeholder="placeholder"
            :openHotkey="openHotkey"
            :closeHotkey="closeHotkey"
            @change="handleChange"
            @selected="handleSelected"
        />

        <!-- EntityDetailsModal is rendered via portal in App.vue -->
        <EntityDetailsModal
            :is-open="showEntityModal"
            :entity-type="selectedEntityType"
            :entity-id="selectedEntityId"
            :entity-name="selectedEntityName"
            :entity-subtitle="selectedEntitySubtitle"
            :entity-data="selectedEntityData"
            :revenue="entityStats.revenue"
            :days-worked="entityStats.daysWorked"
            :euro-per-km="entityStats.euroPerKm"
            :hours-worked="entityStats.hoursWorked"
            @close="handleCloseEntityModal"
        />
    </div>
</template>

<script>
import 'ninja-keys';
import { mapGetters } from 'vuex';
import { getCommandsForNinjaKeys } from '@/lib/commandRegistry';
import { searchAllEntities, createEntityCommand } from '@/services/entitySearchService';
import EntityDetailsModal from '@/components/EntityDetailsModal/index.vue';

export default {
    name: 'CommandPalette',
    components: {
        EntityDetailsModal,
    },
    data() {
        return {
            commands: [],
            entitySearchTimeout: null,
            lastSearchQuery: '',
            isSearching: false,
            showEntityModal: false,
            selectedEntityType: 'driver',
            selectedEntityId: null,
            selectedEntityName: '',
            selectedEntitySubtitle: '',
            selectedEntityData: null,
            entityStats: {
                revenue: 0,
                daysWorked: 0,
                euroPerKm: 0,
                hoursWorked: 0,
                paymentMethodDistribution: {},
            },
        };
    },
    computed: {
        ...mapGetters(['planType']),

        placeholder() {
            return 'Befehl eingeben oder suchen...';
        },

        openHotkey() {
            return 'cmd+k,ctrl+k';
        },

        closeHotkey() {
            return 'escape';
        },
    },
    mounted() {
        this.loadCommands();
        this.setupKeyboardShortcuts();

        this.$nextTick(() => {
            if (this.$refs.ninjaKeys) {
                this.$refs.ninjaKeys.data = this.commands;
                this.$refs.ninjaKeys.addEventListener('open', this.handleNinjaKeysOpen);
                this.$refs.ninjaKeys.addEventListener('close', this.handleNinjaKeysClose);
                this.$refs.ninjaKeys.addEventListener('change', this.handleChange);
            }
        });
    },
    beforeDestroy() {
        this.removeKeyboardShortcuts();

        if (this.$refs.ninjaKeys) {
            this.$refs.ninjaKeys.removeEventListener('open', this.handleNinjaKeysOpen);
            this.$refs.ninjaKeys.removeEventListener('close', this.handleNinjaKeysClose);
        }
    },
    methods: {
        loadCommands() {
            const context = {
                router: this.$router,
                store: this.$store,
                route: this.$route,
            };

            const baseCommands = getCommandsForNinjaKeys(context);

            this.commands = baseCommands;

            this.$nextTick(() => {
                if (this.$refs.ninjaKeys) {
                    this.$refs.ninjaKeys.data = this.commands;
                }
            });
        },

        async handleChange(event) {
            let query = '';
            if (event?.detail?.search) {
                query = event.detail.search;
            } else if (event?.detail?.query) {
                query = event.detail.query;
            } else if (event?.detail?.value) {
                query = event.detail.value;
            } else if (event?.target?.value) {
                query = event.target.value;
            } else if (this.$refs.ninjaKeys?.value) {
                query = this.$refs.ninjaKeys.value;
            } else if (this.$refs.ninjaKeys?.query) {
                query = this.$refs.ninjaKeys.query;
            } else if (this.$refs.ninjaKeys?.shadowRoot) {
                const input = this.$refs.ninjaKeys.shadowRoot.querySelector('input');
                if (input) {
                    query = input.value || '';
                }
            }

            if (!query || query.length < 2) {
                this.loadCommands();
                return;
            }

            clearTimeout(this.entitySearchTimeout);
            this.entitySearchTimeout = setTimeout(async () => {
                await this.performEntitySearch(query);
            }, 300);
        },

        async performEntitySearch(query) {
            if (this.isSearching) {
                return;
            }

            this.isSearching = true;
            this.lastSearchQuery = query;

            try {
                const context = {
                    router: this.$router,
                    store: this.$store,
                    route: this.$route,
                };

                const baseCommands = getCommandsForNinjaKeys(context);

                const entityResults = await searchAllEntities(query, this.$store);

                const entityCommands = [];

                if (entityResults.drivers && entityResults.drivers.length > 0) {
                    entityResults.drivers.forEach((driver) => {
                        const cmd = createEntityCommand('drivers', driver, context);
                        if (cmd) {
                            entityCommands.push(cmd);
                        }
                    });
                }

                if (entityResults.cars && entityResults.cars.length > 0) {
                    entityResults.cars.forEach((car) => {
                        const cmd = createEntityCommand('cars', car, context);
                        if (cmd) {
                            entityCommands.push(cmd);
                        }
                    });
                }

                if (entityResults.concessions && entityResults.concessions.length > 0) {
                    entityResults.concessions.forEach((concession) => {
                        const cmd = createEntityCommand('concessions', concession, context);
                        if (cmd) {
                            entityCommands.push(cmd);
                        }
                    });
                }

                const filteredBaseCommands = baseCommands.filter((cmd) => {
                    const searchText = query.toLowerCase();
                    const titleMatch = cmd.title?.toLowerCase().includes(searchText);
                    const subtitleMatch = cmd.subtitle?.toLowerCase().includes(searchText);
                    const keywordMatch = cmd.keywords
                        ? cmd.keywords.toLowerCase().includes(searchText)
                        : false;
                    return titleMatch || subtitleMatch || keywordMatch;
                });

                this.commands = [...entityCommands, ...filteredBaseCommands];

                this.$nextTick(() => {
                    if (this.$refs.ninjaKeys) {
                        this.$refs.ninjaKeys.data = this.commands;
                    }
                });
            } catch (error) {
            } finally {
                this.isSearching = false;
            }
        },

        handleSelected(event) {
            const command = event.detail?.action || event.detail;
            if (!command) {
                return;
            }

            const context = {
                router: this.$router,
                store: this.$store,
                route: this.$route,
            };

            if (command.id && command.id.startsWith('entity-')) {
                const entityType = command.section?.toLowerCase() || 'driver';
                this.showEntityDetailsModal(command, entityType);
            } else if (command.id === 'current-filter' && command.primaryEntity) {
                this.showEntityDetailsModal(
                    {
                        entityType: command.primaryEntity.type,
                        entityId: command.primaryEntity.id,
                        entityName: command.primaryEntity.name,
                        entityData: null,
                    },
                    command.primaryEntity.type,
                );
            } else {
                if (command.handler && typeof command.handler === 'function') {
                    command.handler();
                } else if (command.action && typeof command.action === 'function') {
                    const result = command.action(context);
                    if (result && result.type === 'open-entity-modal') {
                        this.showEntityDetailsModal(
                            {
                                entityType: result.entityType,
                                entityId: result.entityId,
                                entityName: result.entityName,
                                entityData: null,
                            },
                            result.entityType,
                        );
                    }
                }
            }
        },

        showEntityDetailsModal(command, entityType) {
            const type =
                command.entityType ||
                (entityType === 'drivers'
                    ? 'driver'
                    : entityType === 'cars'
                      ? 'car'
                      : entityType === 'concessions'
                        ? 'concession'
                        : 'driver');

            this.selectedEntityType = type;
            this.selectedEntityName = command.entityName || command.title || '';
            this.selectedEntitySubtitle = command.subtitle || '';

            this.selectedEntityId =
                command.entityId || (command.id ? command.id.match(/entity-\w+-(.+)$/)?.[1] : null);

            this.selectedEntityData = command.entityData || null;

            this.entityStats = {
                revenue: 0,
                daysWorked: 0,
                euroPerKm: 0,
                hoursWorked: 0,
                paymentMethodDistribution: {},
            };

            this.showEntityModal = true;

            this.$forceUpdate();
        },

        handleCloseEntityModal() {
            this.showEntityModal = false;
            this.selectedEntityId = null;
            this.selectedEntityName = '';
            this.selectedEntitySubtitle = '';
            this.selectedEntityData = null;
        },

        setupKeyboardShortcuts() {
            document.addEventListener('keydown', this.handleGlobalKeydown);
        },

        removeKeyboardShortcuts() {
            document.removeEventListener('keydown', this.handleGlobalKeydown);
        },

        handleGlobalKeydown(event) {
            if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
                event.preventDefault();
            }
        },

        handleNinjaKeysOpen() {},

        handleNinjaKeysClose() {},
    },
};
</script>

<style lang="scss" scoped>
.command-palette-wrapper {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 9999;
    pointer-events: none;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: scale(0.95) translateY(-10px);
    }
    to {
        opacity: 1;
        transform: scale(1) translateY(0);
    }
}
</style>
